import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";

import { NudgesService, NudgeType } from "@vowkey/angular/vault";
import { FeatureFlag } from "@vowkey/common/enums/feature-flag.enum";
import { ConfigService } from "@vowkey/common/platform/abstractions/config/config.service";
import { UserId } from "@vowkey/common/types/guid";
import { DialogService } from "@vowkey/components";

import {
  NewExperienceDialogComponent,
  NewExperienceDialogParams,
  NewExperienceDialogResult,
} from "../components/new-experience-dialog/new-experience-dialog.component";

import { Vfo1TourCalloutService } from "./vfo1-tour-callout.service";

/**
 * Decides whether {@link NewExperienceDialogComponent} should announce the redesigned vault.
 *
 * Every client shows the same dialog under the same rules, so the rules live here rather than in
 * each client's vault page. Callers supply their own screenshots and apply whatever additional
 * gating is specific to them — the extension, for instance, waits until its intro carousel has
 * been seen.
 */
@Injectable({ providedIn: "root" })
export class NewExperienceDialogService {
  private readonly configService = inject(ConfigService);
  private readonly dialogService = inject(DialogService);
  private readonly nudgesService = inject(NudgesService);
  private readonly tourCalloutService = inject(Vfo1TourCalloutService);

  /**
   * Opens the dialog when the account predates the redesign and has not been told about it yet.
   * The nudge is dismissed only from one of the dialog's actions, so leaving it any other way shows
   * it again next time. Exploring starts the walkthrough here before the nudge is dismissed.
   *
   * @returns how the dialog was closed, or `null` when it did not open.
   */
  async conditionallyOpen(
    userId: UserId,
    params: Omit<NewExperienceDialogParams, "userId">,
  ): Promise<NewExperienceDialogResult | undefined | null> {
    const vfo1Enabled = await firstValueFrom(
      this.configService.getFeatureFlag$(FeatureFlag.VFO1Foundation),
    );
    if (!vfo1Enabled) {
      return null;
    }

    // This dialog opens without a click, so a server that suppresses onboarding suppresses it.
    const serverSettings = await firstValueFrom(this.configService.serverSettings$);
    if (serverSettings?.suppressOnboardingInterstitials) {
      return null;
    }

    // Vfo1OnboardingNudgeService reports dismissed for accounts created on or after GA, so those
    // users are never told about a change they did not experience.
    const showDialog = await firstValueFrom(
      this.nudgesService.showNudgeSpotlight$(NudgeType.Vfo1NewExperience, userId),
    );
    if (!showDialog) {
      return null;
    }

    const result = await NewExperienceDialogComponent.open(this.dialogService, {
      ...params,
      userId,
    });
    if (result === NewExperienceDialogResult.Explore) {
      // The nudge stays pending until the tour is running so the side nav callout never claims the slot.
      try {
        await this.tourCalloutService.startTour();
      } finally {
        await this.nudgesService.dismissNudge(NudgeType.Vfo1NewExperience, userId);
      }
    }

    return result;
  }
}
